import { DateTime } from 'luxon'

import { Card, Text, Separator } from '@/ui'
import {
  getUserById,
  selectPrimaryMembershipForUser,
} from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { MEMBERSHIP_STATUS } from '@/reducers/auth/userReducer'
import { StripeSubscription } from '@/reducers/subscription.slice'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { TypographyStyles, TextColors } from '@/styles'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'
import { getBillingPeriod } from '@/utils/stripeSubscriptionHelpers'

interface Props {
  userId: number
  subscriptions: StripeSubscription[] | undefined
}

export const SubscriptionEntitlementsPanel = ({
  userId,
  subscriptions,
}: Props) => {
  const user = useReselector(getUserById, userId)
  const primaryMembership = useReselector(
    selectPrimaryMembershipForUser,
    userId
  )

  if (!user) {
    return null
  }

  const stripeSubscription = subscriptions?.find(
    (s) => s.id === primaryMembership?.stripeSubscription
  )

  const getCurrentPeriodLabel = (): string => {
    if (!stripeSubscription) {
      return 'N/A'
    }
    const period = getBillingPeriod(
      stripeSubscription,
      stripeSubscription.primaryPriceId
    )
    if (period.start === null || period.end === null) {
      return 'N/A'
    }
    return `${DateTime.fromSeconds(period.start).toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)} – ${DateTime.fromSeconds(period.end).toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)}`
  }

  return (
    <Card className="w-full">
      <Text variant={TypographyStyles.H2}>Subscription & Entitlements</Text>

      <Separator />

      {/* Billing */}
      <div className="flex flex-col gap-2">
        <Text variant={TypographyStyles.BODY_LG} color={TextColors.HEADER}>
          Billing
        </Text>
        <div className="flex items-start gap-x-2">
          <Text
            variant={TypographyStyles.BODY_MD}
            color={TextColors.HEADER}
            className="w-1/3"
          >
            Status
          </Text>
          <Text variant={TypographyStyles.BODY_MD} className="capitalize">
            {primaryMembership?.status
              ? MEMBERSHIP_STATUS[primaryMembership.status]
              : '—'}
          </Text>
        </div>
        <div className="flex items-start gap-x-2">
          <Text
            variant={TypographyStyles.BODY_MD}
            color={TextColors.HEADER}
            className="w-1/3"
          >
            Billing cycle
          </Text>
          <Text variant={TypographyStyles.BODY_MD}>
            {primaryMembership?.billingCycle ?? '—'}
          </Text>
        </div>
        <div className="flex items-start gap-x-2">
          <Text
            variant={TypographyStyles.BODY_MD}
            color={TextColors.HEADER}
            className="w-1/3"
          >
            Price
          </Text>
          <Text variant={TypographyStyles.BODY_MD}>
            {stripeSubscription
              ? formatCurrencyFromCents(
                  stripeSubscription.items.data[0].plan.amount
                )
              : '—'}
          </Text>
        </div>
        <div className="flex items-start gap-x-2">
          <Text
            variant={TypographyStyles.BODY_MD}
            color={TextColors.HEADER}
            className="w-1/3"
          >
            Current period
          </Text>
          <Text variant={TypographyStyles.BODY_MD}>
            {getCurrentPeriodLabel()}
          </Text>
        </div>
        <div className="flex items-start gap-x-2">
          <Text
            variant={TypographyStyles.BODY_MD}
            color={TextColors.HEADER}
            className="w-1/3"
          >
            Cancels on
          </Text>
          <Text variant={TypographyStyles.BODY_MD}>
            {stripeSubscription?.cancel_at
              ? DateTime.fromSeconds(stripeSubscription.cancel_at).toFormat(
                  DATE_FORMATS_LUXON.DISPLAY_SHORT
                )
              : '—'}
          </Text>
        </div>
        <div className="flex items-start gap-x-2">
          <Text
            variant={TypographyStyles.BODY_MD}
            color={TextColors.HEADER}
            className="w-1/3"
          >
            Cancels at period end
          </Text>
          <Text variant={TypographyStyles.BODY_MD}>
            {stripeSubscription?.cancel_at_period_end ? 'Yes' : '—'}
          </Text>
        </div>
        <div className="flex items-start gap-x-2">
          <Text
            variant={TypographyStyles.BODY_MD}
            color={TextColors.HEADER}
            className="w-1/3"
          >
            Cancelled on
          </Text>
          <Text variant={TypographyStyles.BODY_MD}>
            {primaryMembership?.cancelledAt
              ? DateTime.fromISO(primaryMembership.cancelledAt).toFormat(
                  DATE_FORMATS_LUXON.DISPLAY_SHORT
                )
              : '—'}
          </Text>
        </div>
        <div className="flex items-start gap-x-2">
          <Text
            variant={TypographyStyles.BODY_MD}
            color={TextColors.HEADER}
            className="w-1/3"
          >
            Cancellation reason
          </Text>
          <Text variant={TypographyStyles.BODY_MD}>
            {primaryMembership?.cancellationReason ?? '—'}
          </Text>
        </div>
        <div className="flex items-start gap-x-2">
          <Text
            variant={TypographyStyles.BODY_MD}
            color={TextColors.HEADER}
            className="w-1/3"
          >
            Cancellation comment
          </Text>
          <Text variant={TypographyStyles.BODY_MD}>
            {primaryMembership?.cancellationComment ?? '—'}
          </Text>
        </div>
        <div className="flex items-start gap-x-2">
          <Text
            variant={TypographyStyles.BODY_MD}
            color={TextColors.HEADER}
            className="w-1/3"
          >
            Trial period
          </Text>
          <Text variant={TypographyStyles.BODY_MD}>
            {primaryMembership?.trialStartDate
              ? `${DateTime.fromISO(primaryMembership.trialStartDate).toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)}${primaryMembership.trialEndDate ? ` – ${DateTime.fromISO(primaryMembership.trialEndDate).toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)}` : ''}`
              : '—'}
          </Text>
        </div>
        <div className="flex items-start gap-x-2">
          <Text
            variant={TypographyStyles.BODY_MD}
            color={TextColors.HEADER}
            className="w-1/3"
          >
            Annual commitment
          </Text>
          <Text variant={TypographyStyles.BODY_MD}>
            {primaryMembership?.hasAnnualCommitment ? 'Yes' : '—'}
          </Text>
        </div>
      </div>

      <Separator />

      {/* Feature Entitlements */}
      <div className="flex flex-col gap-2">
        <Text variant={TypographyStyles.BODY_LG} color={TextColors.HEADER}>
          Feature Entitlements
        </Text>
        {user.featurePermissions && user.featurePermissions.length > 0 ? (
          user.featurePermissions.map((fp) => (
            <div key={fp.id} className="flex items-start gap-x-2">
              <Text
                variant={TypographyStyles.BODY_MD}
                color={TextColors.HEADER}
                className="w-1/3"
              >
                {fp.name}
              </Text>
              <Text variant={TypographyStyles.BODY_MD}>{fp.description}</Text>
            </div>
          ))
        ) : (
          <Text variant={TypographyStyles.BODY_MD}>—</Text>
        )}
      </div>

      <Separator />

      {/* Financial Advisory */}
      <div className="flex flex-col gap-2">
        <Text variant={TypographyStyles.BODY_LG} color={TextColors.HEADER}>
          Financial Advisory
        </Text>
        <div className="flex items-start gap-x-2">
          <Text
            variant={TypographyStyles.BODY_MD}
            color={TextColors.HEADER}
            className="w-1/3"
          >
            Subscription status
          </Text>
          <Text variant={TypographyStyles.BODY_MD}>
            {user.advisorySubscriptionStatus ?? '—'}
          </Text>
        </div>
      </div>

      <Separator />

      {/* Payroll */}
      <div className="flex flex-col gap-2">
        <Text variant={TypographyStyles.BODY_LG} color={TextColors.HEADER}>
          Payroll
        </Text>
        <div className="flex items-start gap-x-2">
          <Text
            variant={TypographyStyles.BODY_MD}
            color={TextColors.HEADER}
            className="w-1/3"
          >
            Gusto plan
          </Text>
          <Text variant={TypographyStyles.BODY_MD}>
            {user.gustoPlan ?? '—'}
          </Text>
        </div>
      </div>
    </Card>
  )
}
